import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// Get all published trips (only from approved hosts)
export const getPublishedTrips = async () => {
  const tripsRef = collection(db, 'trips');
  const q = query(
    tripsRef,
    where('status', '==', 'published'),
    orderBy('startDate', 'asc')
  );
  const snapshot = await getDocs(q);
  const trips = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
  
  // Filter trips to only include those from approved hosts
  // We need to check user documents for approvedAsHost flag
  const { getUserData } = await import('../auth');
  const approvedTrips = await Promise.all(
    trips.map(async (trip) => {
      try {
        const hostData = await getUserData(trip.hostId);
        return hostData?.approvedAsHost === true ? trip : null;
      } catch (error) {
        console.error(`Error checking host approval for trip ${trip.id}:`, error);
        return null; // Exclude trip if we can't verify approval
      }
    })
  );
  
  return approvedTrips.filter(trip => trip !== null);
};

// Get trip by ID
export const getTrip = async (tripId) => {
  const tripDoc = await getDoc(doc(db, 'trips', tripId));
  if (!tripDoc.exists()) {
    return null;
  }
  return { id: tripDoc.id, ...tripDoc.data() };
};

// Get trips by host
export const getHostTrips = async (hostId) => {
  const tripsRef = collection(db, 'trips');
  const q = query(
    tripsRef,
    where('hostId', '==', hostId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
};

// Create trip (host only)
export const createTrip = async (tripData) => {
  const tripsRef = collection(db, 'trips');
  // Use status from tripData if provided, otherwise default to 'draft'
  const status = tripData.status || 'draft';
  const docRef = await addDoc(tripsRef, {
    ...tripData,
    status: status,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// Update trip (host only)
export const updateTrip = async (tripId, updates) => {
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

// Publish trip (host can publish their own trips)
export const publishTrip = async (tripId) => {
  await updateTrip(tripId, { status: 'published' });
};

// Unpublish trip (host can unpublish their own trips)
export const unpublishTrip = async (tripId) => {
  await updateTrip(tripId, { status: 'draft' });
};

// Delete trip (host can delete their own trips)
export const deleteTrip = async (tripId) => {
  const tripRef = doc(db, 'trips', tripId);
  await deleteDoc(tripRef);
};

